import { inject, injectable, Store } from '@servicetitan/react-ioc';

import { makeObservable, observable } from 'mobx';

import { CultureApi, CultureInfoData } from './culture.api';

export interface PhoneFormat {
    PhoneFormatInfo: {
        Pattern: RegExp;
        Format: string;
        HiddenNumberFormat?: string;
    };
    MobileFormatInfo: {
        Pattern: RegExp;
        Format: string;
        HiddenNumberFormat?: string;
    };
    PhoneWithExtNumberFormatInfo: {
        Pattern: RegExp;
        Format: string;
        HiddenNumberFormat?: string;
    };
    PhoneMask: string;
    MobileMask: string;
    SimplePhoneMask: string;
    NonStrictPhoneMask: string;
    FaxMask: string;
    PhonePlaceholder: string;
    SimplePhonePlaceholder: string;
    FaxPlaceholder: string;
}

export interface Culture extends CultureInfoData {
    PhoneFormat: PhoneFormat;
}

export interface ICultureStore {
    culture: Culture;
}

@injectable()
export class CultureStore extends Store implements ICultureStore {
    @observable culture!: Culture;

    constructor(@inject(CultureApi) private readonly api: CultureApi) {
        super();
        makeObservable(this);
    }

    async initialize() {
        const cultureData = (await this.api.get()).data;

        this.culture = {
            ...cultureData,
            PhoneFormat: getPhoneFormatByCulture(cultureData.PhoneCulture ?? cultureData.Name),
        };
    }
}

const getPhonePlaceholderFromMask = (mask: string) => mask.replace(/9/g, '_').replace('?', '');

export const getPhoneFormatByCulture = (name: string) => {
    const cultureNameLowerCase = name.toLowerCase();

    const is = {
        us: cultureNameLowerCase === 'en-us',
        ca: cultureNameLowerCase === 'en-ca',
        gb: cultureNameLowerCase === 'en-gb',
        au: cultureNameLowerCase === 'en-au',
        za: cultureNameLowerCase === 'en-za',
    };

    const phoneFormat: Omit<
        PhoneFormat,
        'PhonePlaceholder' | 'SimplePhonePlaceholder' | 'FaxPlaceholder'
    > = {
        PhoneMask: '?9999999999 x99999',
        MobileMask: '?9999999999 x99999',
        NonStrictPhoneMask: '?9999999999 x99999',
        SimplePhoneMask: '?9999999999',
        FaxMask: '?9999999999',
        PhoneFormatInfo: {
            Pattern: /(\d{10})/,
            Format: '$1',
        },
        MobileFormatInfo: {
            Pattern: /(\d{10})/,
            Format: '$1',
        },
        PhoneWithExtNumberFormatInfo: {
            Pattern: /(\d{10})(\d+)/,
            Format: '$1 x$2',
        },
    };
    if (is.us || is.ca) {
        phoneFormat.PhoneMask = '(999) 999-9999? x99999';
        phoneFormat.MobileMask = '(999) 999-9999 x99999';
        phoneFormat.NonStrictPhoneMask = '?(?999) 999-9999 x99999';
        phoneFormat.SimplePhoneMask = '(999) 999-9999?';
        phoneFormat.FaxMask = '(999) 999-9999';
        phoneFormat.PhoneFormatInfo = {
            Pattern: /(\d{3})(\d{3})(\d{4})/,
            Format: '($1) $2-$3',
            HiddenNumberFormat: '($1) $2-XXXX',
        };
        phoneFormat.MobileFormatInfo = {
            Pattern: /(\d{3})(\d{3})(\d{4})/,
            Format: '($1) $2-$3',
            HiddenNumberFormat: '($1) $2-XXXX',
        };
        phoneFormat.PhoneWithExtNumberFormatInfo = {
            Pattern: /(\d{3})(\d{3})(\d{4})(\d+)/,
            Format: '($1) $2-$3 x$4',
            HiddenNumberFormat: '($1) $2-XXXX x$4',
        };
    } else if (is.gb) {
        phoneFormat.PhoneMask = '?99999999999 x99999';
        phoneFormat.MobileMask = '?99999999999 x99999';
        phoneFormat.NonStrictPhoneMask = '?99999999999 x99999';
        phoneFormat.SimplePhoneMask = '?99999999999';
        phoneFormat.FaxMask = '?99999999999';
        phoneFormat.PhoneFormatInfo = {
            Pattern: /(\d{3})(.*)/,
            Format: '$1$2',
            HiddenNumberFormat: '$1XXXX',
        };
        phoneFormat.MobileFormatInfo = {
            Pattern: /(\d{3})(.*)/,
            Format: '$1$2',
            HiddenNumberFormat: '$1XXXX',
        };
        phoneFormat.PhoneWithExtNumberFormatInfo = {
            Pattern: /(\d{3})(.*)/,
            Format: '$1$2',
            HiddenNumberFormat: '$1XXXX',
        };
    } else if (is.au) {
        phoneFormat.PhoneMask = '99 9999 9999? x99999';
        phoneFormat.MobileMask = '9999 999 999';
        phoneFormat.NonStrictPhoneMask = '?99 9999 9999 x99999';
        phoneFormat.SimplePhoneMask = '99 9999 9999';
        phoneFormat.FaxMask = '99 9999 9999';
        phoneFormat.PhoneFormatInfo = {
            Pattern: /(\d{2})(\d{4})(\d{4})/,
            Format: '$1 $2 $3',
            HiddenNumberFormat: '$1 $2 XXXX',
        };
        phoneFormat.MobileFormatInfo = {
            Pattern: /(\d{4})(\d{3})(\d{3})/,
            Format: '$1 $2 $3',
            HiddenNumberFormat: '$1 $2 XXX',
        };
        phoneFormat.PhoneWithExtNumberFormatInfo = {
            Pattern: /(\d{2})(\d{4})(\d{4})(\d+)/,
            Format: '$1 $2 $3 x$4',
            HiddenNumberFormat: '$1 $2 XXXX x$4',
        };
    }

    return {
        ...phoneFormat,
        PhonePlaceholder: getPhonePlaceholderFromMask(phoneFormat.PhoneMask),
        MobilePlaceholder: getPhonePlaceholderFromMask(phoneFormat.MobileMask),
        SimplePhonePlaceholder: getPhonePlaceholderFromMask(phoneFormat.SimplePhoneMask),
        FaxPlaceholder: getPhonePlaceholderFromMask(phoneFormat.FaxMask),
    };
};
